/**
 * SKU Flavor Selector with Color Swatches
 *
 * Fetches and displays explicit product groups from the app
 * Provides a variant-like selector with color swatches and taste labels
 *
 * Metafields used:
 * - custom.color_swatch (color): Background color for the swatch
 * - custom.variant_label (single line text): App group option label
 */

class SKUFlavorSelector {
  constructor(element) {
    this.element = element;
    this.sku = element.dataset.sku;
    this.productId = element.dataset.productId;
    this.shopDomain = element.dataset.shopDomain;
    this.market = element.dataset.market || "";
    this.locale = element.dataset.locale || "";
    this.rootUrl = (element.dataset.root || "/").replace(/\/$/, "");
    this.labels = {
      menuTitle: element.dataset.labelMenuTitle,
      close: element.dataset.labelClose,
      count: element.dataset.labelCount,
      limitedRow: element.dataset.labelLimitedRow,
      limitedTag: element.dataset.labelLimitedTag,
      currentSelection: element.dataset.labelCurrentSelection,
      wasPrice: element.dataset.labelWasPrice,
      currentlyViewing: element.dataset.labelCurrentlyViewing,
      viewFlavor: element.dataset.labelViewFlavor,
    };

    // Get current product's metafield data from data attributes
    this.currentColor = element.dataset.currentColor || "";
    this.currentImage = element.dataset.currentImage || "";
    this.displayStyle = element.dataset.displayStyle || "swatch";

    this.loadingElement = element.querySelector(
      ".sku-flavor-selector__loading"
    );
    this.optionsElement = element.querySelector(
      ".sku-flavor-selector__options"
    );
    this.errorElement = element.querySelector(".sku-flavor-selector__error");
    this.errorText = element.querySelector(".sku-flavor-selector__error-text");
    this.currentFlavorElement = element.querySelector(
      ".sku-flavor-selector__current-flavor"
    );
    this.currentFlavorName = null;

    this.cache = this.getCache();

    this.init();
  }

  /**
   * Initialize the selector
   * Component stays collapsed until all data is ready to prevent layout shift
   */
  async init() {
    if (!this.productId) {
      console.warn("SKU Flavor Selector: No product ID available");
      this.removeComponent();
      return;
    }

    console.log("SKU Flavor Selector: Initializing with SKU:", this.sku);
    console.log("SKU Flavor Selector: Shop domain:", this.shopDomain);
    console.log("SKU Flavor Selector: Market:", this.market);

    try {
      const data = await this.fetchProducts();

      console.log("SKU Flavor Selector: API Response:", data);

      // Handle response format (API returns {products: []} directly)
      const products = data.products || [];
      const totalProducts = products.length;

      if (totalProducts === 0) {
        console.error(
          "SKU Flavor Selector: No products found for SKU:",
          this.sku
        );
        this.removeComponent();
        return;
      }

      if (totalProducts <= 1) {
        // Only one product, hide the selector entirely
        console.log(
          "SKU Flavor Selector: Only one product found, hiding selector"
        );
        this.removeComponent();
        return;
      }

      // The app owns membership, labels and order. An excluded current product
      // must not display a selector for a different market's siblings.
      if (!products.some(product => product.current && this.extractNumericId(product.id) === this.productId)) {
        this.removeComponent();
        return;
      }
      this.renderOptions(products);

      // Wait for next frame to ensure DOM is updated, then expand
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.expandComponent();
        });
      });
    } catch (error) {
      console.error("SKU Flavor Selector Error:", error);
      console.error("Error details:", error.message, error.stack);
      this.removeComponent();
    }
  }

  /**
   * Fetch products from the API with caching
   */
  async fetchProducts() {
    // Check cache first (scoped per market and locale so a shopper switching
    // markets or languages, or the shared cache across
    // heimatgut.com/heartfulsnacks.com, never serves the wrong flavor list)
    const cacheKey = `group-v1-${this.productId}-${this.market}-${this.locale}`;
    const cached = this.cache[cacheKey];

    if (cached && this.isCacheValid(cached.timestamp)) {
      console.log("Using cached data for SKU:", this.sku);
      return cached.data;
    }

    // Fetch from API using GET (works better with Shopify App Proxy)
    const apiUrl = `/apps/heimatgut-sku-flavor/products/group`;
    const getUrl = `${apiUrl}?id=${encodeURIComponent(this.productId)}&market=${encodeURIComponent(this.market)}&locale=${encodeURIComponent(this.locale)}`;

    console.log("SKU Flavor Selector: Fetching from API:", getUrl);

    try {
      const response = await fetch(getUrl, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      });

      console.log(
        "SKU Flavor Selector: Response status:",
        response.status,
        response.statusText
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("SKU Flavor Selector: Error response body:", errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("SKU Flavor Selector: Parsed response data:", data);

      // Cache the response
      this.setCache(cacheKey, data);

      return data;
    } catch (error) {
      console.error("SKU Flavor Selector: Request failed:", error);
      throw error;
    }
  }

  /**
   * Render product options as color swatches with taste labels
   * Group order is configured in the app
   */
  renderOptions(products) {
    const currentProductId = this.productId.toString();

    // Preserve the order configured in the app, including size variants.

    // Render option HTML for a product based on display style
    const renderOption = (product) => {
      const productId = this.extractNumericId(product.id);
      const isCurrent = product.current === true;

      const price = this.formatMoney(product.price);
      const compareAtPrice =
        product.compare_at_price
          ? this.formatMoney(product.compare_at_price)
          : null;
      const hasDiscount =
        compareAtPrice &&
        parseFloat(product.compare_at_price) > parseFloat(product.price);

      const flavorName = this.escapeHtml(product.variant_label || product.title);

      // Get color swatch, taste label, and image from metafields
      let colorSwatch = "";
      const tasteLabel = flavorName;
      let productImage = "";

      if (isCurrent) {
        colorSwatch = this.currentColor;
        productImage = this.currentImage;
      } else {
        colorSwatch = product.metafields?.custom?.color_swatch?.value ||
                      product.color_swatch ||
                      "";
        productImage = product.featured_image || "";
      }

      if (!colorSwatch) {
        colorSwatch = this.getColorFromTitle(flavorName);
      }

      const classes = [
        "sku-flavor-selector__option",
        `sku-flavor-selector__option--${this.displayStyle}`,
        isCurrent ? "sku-flavor-selector__option--current" : "",
      ]
        .filter(Boolean)
        .join(" ");

      // Render based on display style
      let innerContent = "";

      if (this.displayStyle === "pill") {
        // Pill style - text only
        innerContent = `
          <span class="sku-flavor-selector__pill-label">${tasteLabel}</span>
          ${isCurrent ? `
            <span class="sku-flavor-selector__pill-check" aria-hidden="true">
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          ` : ""}
        `;
      } else if (this.displayStyle === "image") {
        // Image style
        innerContent = `
          <div class="sku-flavor-selector__image-wrapper">
            ${productImage ? `
              <img src="${this.optimizeImageUrl(productImage, 100)}" alt="${flavorName}" class="sku-flavor-selector__product-image" loading="lazy">
            ` : `
              <div class="sku-flavor-selector__image-placeholder">
                <span class="sku-flavor-selector__option-initial">${flavorName.charAt(0)}</span>
              </div>
            `}
            ${isCurrent ? `
              <span class="sku-flavor-selector__option-checkmark" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            ` : ""}
          </div>
          <span class="sku-flavor-selector__taste-label">${tasteLabel}</span>
        `;
      } else {
        // Default: swatch style
        innerContent = `
          <div class="sku-flavor-selector__swatch-wrapper">
            <div
              class="sku-flavor-selector__color-swatch"
              style="${colorSwatch ? `background-color: ${colorSwatch};` : ''}"
            >
              ${isCurrent ? `
                <span class="sku-flavor-selector__option-checkmark" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
              ` : ""}
            </div>
            <span class="sku-flavor-selector__taste-label">${tasteLabel}</span>
          </div>
        `;
      }

      return `
        <a
          href="${isCurrent ? "#" : this.productUrl(product.handle)}"
          class="${classes}"
          role="radio"
          aria-checked="${isCurrent}"
          aria-label="${this.escapeHtml(product.title)}${isCurrent ? ` (${this.labels.currentSelection})` : ""}"
          ${isCurrent ? 'aria-current="page"' : ""}
          data-product-id="${productId}"
          data-flavor-name="${flavorName}"
          data-taste-label="${tasteLabel}"
          tabindex="0"
        >
          ${innerContent}
          <span class="sku-flavor-selector__sr-only">
            ${flavorName}. ${price}${hasDiscount ? `, ${this.labels.wasPrice.replace("__PRICE__", compareAtPrice)}` : ""}. ${isCurrent ? this.labels.currentlyViewing : this.labels.viewFlavor}
          </span>
        </a>
      `;
    };

    // Build the HTML
    let html = '';

    if (this.displayStyle === 'dropdown') {
      // Dropdown mode — bottom-sheet style with product images
      const allProducts = products;
      const totalCount = allProducts.length;
      const currentProduct = allProducts.find(p => this.extractNumericId(p.id) === currentProductId);
      const currentTaste = this.escapeHtml(currentProduct?.variant_label || currentProduct?.title || '');
      const currentImg = this.currentImage || (currentProduct?.featured_image) || '';

      const renderDropdownOption = (product) => {
        const productId = this.extractNumericId(product.id);
        const isCurrent = product.current === true;
        const flavorName = this.escapeHtml(product.variant_label || product.title);
        const tasteLabel = flavorName;
        let productImage = '';

        if (isCurrent) {
          productImage = this.currentImage || '';
        } else {
          productImage = product.featured_image || '';
        }

        return `
          <button
            class="sku-flavor-selector__dropdown-item${isCurrent ? ' sku-flavor-selector__dropdown-item--current' : ''}"
            data-handle="${product.handle}"
            data-product-id="${productId}"
            data-flavor-name="${flavorName}"
            data-taste-label="${tasteLabel}"
            role="option"
            aria-selected="${isCurrent}"
            type="button"
          >
            <span class="sku-flavor-selector__dropdown-img">
              ${productImage ? `<img src="${this.optimizeImageUrl(productImage, 120)}" alt="${tasteLabel}" loading="lazy">` : `<span class="sku-flavor-selector__dropdown-img-placeholder">${tasteLabel.charAt(0)}</span>`}
            </span>
            <span class="sku-flavor-selector__dropdown-text">
              <span class="sku-flavor-selector__dropdown-label">${tasteLabel}</span>
            </span>
            ${isCurrent ? '<svg class="sku-flavor-selector__dropdown-check" width="18" height="18" viewBox="0 0 16 16" fill="none"><path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' : ''}
          </button>
        `;
      };

      html = `
        <div class="sku-flavor-selector__dropdown">
          <button class="sku-flavor-selector__dropdown-trigger" type="button" aria-haspopup="listbox" aria-expanded="false">
            <span class="sku-flavor-selector__dropdown-img">
              ${currentImg ? `<img src="${this.optimizeImageUrl(currentImg, 80)}" alt="${currentTaste}">` : ''}
            </span>
            <span class="sku-flavor-selector__dropdown-text">
              <span class="sku-flavor-selector__dropdown-value">${currentTaste}</span>
              <span class="sku-flavor-selector__dropdown-count">${this.labels.count.replace("__COUNT__", totalCount)}</span>
            </span>
            <svg class="sku-flavor-selector__dropdown-arrow" width="18" height="18" viewBox="0 0 16 16" fill="none"><path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
          <div class="sku-flavor-selector__dropdown-overlay"></div>
          <div class="sku-flavor-selector__dropdown-menu" role="listbox">
            <div class="sku-flavor-selector__dropdown-menu-header">
              <span class="sku-flavor-selector__dropdown-menu-title">${this.labels.menuTitle}</span>
              <button class="sku-flavor-selector__dropdown-close" type="button" aria-label="${this.labels.close}">
                <svg width="20" height="20" viewBox="0 0 16 16" fill="none"><path d="M12 4L4 12M4 4l8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
              </button>
            </div>
            <div class="sku-flavor-selector__dropdown-menu-items">
              ${products.map(renderDropdownOption).join('')}
            </div>
          </div>
        </div>
      `;

      this.optionsElement.innerHTML = html;

      // Setup dropdown behavior
      this.setupDropdown();
    } else {
      // Swatch / Image / Pill modes — render rows
      // Regular products row
      if (products.length > 0) {
        html += `
          <div class="sku-flavor-selector__row sku-flavor-selector__row--regular">
            <div class="sku-flavor-selector__row-swatches">
              ${products.map(renderOption).join('')}
            </div>
          </div>
        `;
      }

      this.optionsElement.innerHTML = html;
    }

    // Initialize current flavor name display
    this.initializeFlavorNameDisplay(products);

    // Add keyboard navigation
    this.setupKeyboardNavigation();

    // Add hover event listeners for flavor name display
    this.setupFlavorNameHover();
  }

  /**
   * Get a fallback color based on flavor name
   */
  getColorFromTitle(flavorName) {
    const lowerName = flavorName.toLowerCase();

    // Common flavor-to-color mappings (German and English)
    const colorMap = {
      // Chocolate/Cocoa
      'schokolade': '#5D3A1A',
      'chocolate': '#5D3A1A',
      'schoko': '#5D3A1A',
      'kakao': '#4A2C2A',
      'cocoa': '#4A2C2A',
      'dark': '#2C1810',
      'dunkel': '#2C1810',

      // Caramel
      'karamell': '#C68642',
      'caramel': '#C68642',

      // Vanilla
      'vanille': '#F3E5AB',
      'vanilla': '#F3E5AB',

      // Strawberry
      'erdbeere': '#FC5A8D',
      'strawberry': '#FC5A8D',

      // Blueberry
      'blaubeere': '#4F86F7',
      'blueberry': '#4F86F7',
      'heidelbeere': '#4F86F7',

      // Apple
      'apfel': '#8DB600',
      'apple': '#8DB600',

      // Orange
      'orange': '#FF8C00',

      // Lemon
      'zitrone': '#FFF44F',
      'lemon': '#FFF44F',

      // Mint
      'minze': '#98FF98',
      'mint': '#98FF98',

      // Peanut
      'erdnuss': '#C19A6B',
      'peanut': '#C19A6B',

      // Coconut
      'kokos': '#FFFAF0',
      'coconut': '#FFFAF0',

      // Hazelnut
      'haselnuss': '#8B4513',
      'hazelnut': '#8B4513',

      // Coffee
      'kaffee': '#6F4E37',
      'coffee': '#6F4E37',

      // Cinnamon
      'zimt': '#D2691E',
      'cinnamon': '#D2691E',
    };

    // Check if any keyword matches
    for (const [keyword, color] of Object.entries(colorMap)) {
      if (lowerName.includes(keyword)) {
        return color;
      }
    }

    // Default fallback color
    return '#E8E0D8';
  }

  /**
   * Initialize the current flavor name display
   */
  initializeFlavorNameDisplay(products) {
    if (!this.currentFlavorElement) return;

    const currentProductId = this.productId.toString();
    const currentProduct = products.find(
      (product) =>
        this.extractNumericId(product.id).toString() === currentProductId
    );

    if (currentProduct) {
      this.currentFlavorName = currentProduct.variant_label || currentProduct.title;
      this.currentFlavorElement.textContent = this.currentFlavorName;
    }
  }

  escapeHtml(value) {
    const span = document.createElement('span');
    span.textContent = value || '';
    return span.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /**
   * Setup hover event listeners to update flavor name display
   */
  setupFlavorNameHover() {
    if (!this.currentFlavorElement) return;

    const options = Array.from(
      this.optionsElement.querySelectorAll(".sku-flavor-selector__option")
    );

    options.forEach((option) => {
      const flavorName = option.dataset.flavorName;

      if (!flavorName) return;

      // Only handle hover on desktop (not touch devices)
      if (window.matchMedia("(hover: hover)").matches) {
        option.addEventListener("mouseenter", () => {
          this.currentFlavorElement.textContent = flavorName;
        });

        option.addEventListener("mouseleave", () => {
          if (this.currentFlavorName) {
            this.currentFlavorElement.textContent = this.currentFlavorName;
          }
        });

        // Also handle focus for keyboard navigation
        option.addEventListener("focus", () => {
          this.currentFlavorElement.textContent = flavorName;
        });

        option.addEventListener("blur", () => {
          if (this.currentFlavorName) {
            this.currentFlavorElement.textContent = this.currentFlavorName;
          }
        });
      }
    });
  }

  /**
   * Setup dropdown toggle, item selection, and outside-click closing
   */
  setupDropdown() {
    const dropdown = this.element.querySelector('.sku-flavor-selector__dropdown');
    const trigger = dropdown.querySelector('.sku-flavor-selector__dropdown-trigger');
    const menu = dropdown.querySelector('.sku-flavor-selector__dropdown-menu');
    const overlay = dropdown.querySelector('.sku-flavor-selector__dropdown-overlay');
    const closeBtn = dropdown.querySelector('.sku-flavor-selector__dropdown-close');
    const items = Array.from(menu.querySelectorAll('.sku-flavor-selector__dropdown-item'));
    let isOpen = false;

    // Portal overlay + menu to <body> so they escape any parent
    // stacking context, overflow:hidden, or transforms.
    document.body.appendChild(overlay);
    document.body.appendChild(menu);

    const isDesktop = () => window.matchMedia('(min-width: 750px)').matches;

    // Reposition the desktop dropdown menu relative to the trigger
    const positionMenu = () => {
      const rect = trigger.getBoundingClientRect();
      menu.style.top = `${rect.bottom + 4}px`;
      menu.style.left = `${rect.left}px`;
      menu.style.width = `${rect.width}px`;
    };

    // Scroll/resize handler: reposition on desktop, close if trigger is out of view
    const onScrollOrResize = () => {
      if (!isOpen || !isDesktop()) return;
      const rect = trigger.getBoundingClientRect();
      // Close the menu if the trigger has scrolled out of the viewport
      if (rect.bottom < 0 || rect.top > window.innerHeight) {
        closeMenu();
        return;
      }
      positionMenu();
    };

    const openMenu = () => {
      isOpen = true;
      trigger.setAttribute('aria-expanded', 'true');

      if (isDesktop()) {
        positionMenu();
        // Keep the menu anchored to the trigger while scrolling/resizing
        window.addEventListener('scroll', onScrollOrResize, { passive: true });
        window.addEventListener('resize', onScrollOrResize, { passive: true });
      } else {
        // Mobile bottom-sheet: reset any inline desktop positioning
        menu.style.top = '';
        menu.style.left = '';
        menu.style.width = '';
        document.body.style.overflow = 'hidden';
        // Hide sticky ATC so it doesn't sit on top of the bottom sheet
        document.querySelectorAll('.lumin-sticky-atc, .sticky-atc').forEach(
          el => el.style.setProperty('display', 'none', 'important')
        );
        // Show overlay, then animate in
        overlay.classList.add('sku-flavor-selector__dropdown-overlay--visible');
        overlay.offsetHeight;
        overlay.classList.add('sku-flavor-selector__dropdown-overlay--open');
      }

      menu.classList.add('sku-flavor-selector__dropdown-menu--open');
    };

    const closeMenu = () => {
      if (!isOpen) return;
      isOpen = false;
      menu.classList.remove('sku-flavor-selector__dropdown-menu--open');
      trigger.setAttribute('aria-expanded', 'false');

      // Always clean up desktop scroll/resize listeners
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);

      if (!isDesktop()) {
        overlay.classList.remove('sku-flavor-selector__dropdown-overlay--open');
        document.body.style.overflow = '';
        // Restore sticky ATC
        document.querySelectorAll('.lumin-sticky-atc, .sticky-atc').forEach(
          el => el.style.removeProperty('display')
        );
        // Remove overlay pointer-events after transition ends
        const onEnd = () => {
          if (!isOpen) overlay.classList.remove('sku-flavor-selector__dropdown-overlay--visible');
          overlay.removeEventListener('transitionend', onEnd);
        };
        overlay.addEventListener('transitionend', onEnd);
      }
    };

    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      isOpen ? closeMenu() : openMenu();
    });

    overlay.addEventListener('click', closeMenu);
    closeBtn.addEventListener('click', closeMenu);

    // Close desktop dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!isOpen || !isDesktop()) return;
      if (!menu.contains(e.target) && !trigger.contains(e.target)) {
        closeMenu();
      }
    });

    items.forEach((item) => {
      item.addEventListener('click', () => {
        const handle = item.dataset.handle;
        const isCurrent = item.classList.contains('sku-flavor-selector__dropdown-item--current');
        if (!isCurrent && handle) {
          window.location.href = this.productUrl(handle);
        }
        closeMenu();
      });
    });

    menu.addEventListener('keydown', (e) => {
      const focusable = items.filter(i => !i.hidden);
      const idx = focusable.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        focusable[(idx + 1) % focusable.length]?.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        focusable[(idx - 1 + focusable.length) % focusable.length]?.focus();
      } else if (e.key === 'Escape') {
        closeMenu();
        trigger.focus();
      }
    });
  }

  /**
   * Build a product URL inside the shopper's locale (e.g. /en/products/...)
   */
  productUrl(handle) {
    return `${this.rootUrl}/products/${handle}`;
  }

  /**
   * Extract numeric ID from Shopify GID
   */
  extractNumericId(gid) {
    if (!gid) return null;
    const match = gid.match(/\/(\d+)$/);
    return match ? match[1] : gid.toString();
  }

  /**
   * Optimize Shopify image URL for specific width
   */
  optimizeImageUrl(url, width) {
    if (!url) return "";

    // Remove existing size parameter if present
    const cleanUrl = url.replace(
      /_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master)\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?v=\d+)?$/i,
      ".$2$3"
    );

    // Add new size parameter
    return cleanUrl.replace(
      /\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?v=\d+)?$/i,
      `_${width}x.$1$2`
    );
  }

  /**
   * Format money according to Shopify's format
   */
  formatMoney(amount) {
    if (typeof amount === "string") {
      amount = parseFloat(amount);
    }

    // Check if window.Shopify.formatMoney is available (from lumin-main.js)
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      const cents = Math.round(amount * 100);
      return window.Shopify.formatMoney(cents, window.lumintheme?.moneyFormat);
    }

    // Fallback formatting
    const formatted = amount.toFixed(2);

    // Try to get currency symbol from shop configuration
    const currency = this.getCurrencySymbol();
    return `${currency}${formatted}`;
  }

  /**
   * Get currency symbol (fallback method)
   */
  getCurrencySymbol() {
    // Try to detect from existing price elements on page
    const priceElement = document.querySelector('[class*="price"]');
    if (priceElement) {
      const priceText = priceElement.textContent;
      const symbols = ["$", "€", "£", "¥", "kr", "CHF"];
      for (const symbol of symbols) {
        if (priceText.includes(symbol)) {
          return symbol;
        }
      }
    }
    return "$"; // Default fallback
  }

  /**
   * Setup keyboard navigation for accessibility
   */
  setupKeyboardNavigation() {
    const options = Array.from(
      this.optionsElement.querySelectorAll(".sku-flavor-selector__option")
    );

    options.forEach((option, index) => {
      option.addEventListener("keydown", (e) => {
        let targetIndex;

        switch (e.key) {
          case "ArrowRight":
          case "ArrowDown":
            e.preventDefault();
            targetIndex = (index + 1) % options.length;
            options[targetIndex].focus();
            break;

          case "ArrowLeft":
          case "ArrowUp":
            e.preventDefault();
            targetIndex = (index - 1 + options.length) % options.length;
            options[targetIndex].focus();
            break;

          case "Home":
            e.preventDefault();
            options[0].focus();
            break;

          case "End":
            e.preventDefault();
            options[options.length - 1].focus();
            break;

          case "Enter":
          case " ":
            e.preventDefault();
            if (
              !option.classList.contains(
                "sku-flavor-selector__option--current"
              ) &&
              !option.classList.contains(
                "sku-flavor-selector__option--unavailable"
              )
            ) {
              window.location.href = option.href;
            }
            break;
        }
      });
    });
  }

  /**
   * Cache management
   */
  getCache() {
    try {
      const cached = localStorage.getItem("sku-flavor-selector-cache");
      return cached ? JSON.parse(cached) : {};
    } catch (error) {
      console.warn("Failed to read cache:", error);
      return {};
    }
  }

  setCache(key, data) {
    try {
      this.cache[key] = {
        data: data,
        timestamp: Date.now(),
      };
      localStorage.setItem(
        "sku-flavor-selector-cache",
        JSON.stringify(this.cache)
      );
    } catch (error) {
      console.warn("Failed to write cache:", error);
    }
  }

  isCacheValid(timestamp) {
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
    return Date.now() - timestamp < CACHE_DURATION;
  }

  /**
   * UI State Management - Layout Shift Prevention
   *
   * Strategy:
   * - Component starts collapsed (height: 0)
   * - Content is rendered while collapsed
   * - expandComponent() smoothly animates to natural height
   * - removeComponent() removes from DOM without causing shift
   */

  /**
   * Expand the component smoothly after content is ready
   * Uses CSS transitions for smooth height animation
   */
  expandComponent() {
    if (!this.element) return;

    // Get the natural height of the content
    const inner = this.element.querySelector('.sku-flavor-selector__inner');
    if (!inner) return;

    // Temporarily make visible to measure
    this.element.style.height = 'auto';
    const naturalHeight = this.element.offsetHeight;

    // Reset to 0 for animation
    this.element.style.height = '0px';

    // Force reflow
    this.element.offsetHeight;

    // Animate to natural height
    this.element.style.height = `${naturalHeight}px`;

    // Switch classes for the transition
    this.element.classList.remove('sku-flavor-selector--collapsed');
    this.element.classList.add('sku-flavor-selector--expanded');
    this.element.setAttribute('aria-hidden', 'false');

    // After transition, set height to auto for responsive behavior
    const onTransitionEnd = () => {
      this.element.style.height = 'auto';
      this.element.removeEventListener('transitionend', onTransitionEnd);
    };
    this.element.addEventListener('transitionend', onTransitionEnd);

    console.log("SKU Flavor Selector: Component expanded");
  }

  /**
   * Remove the component from DOM entirely
   * Called when there's no data or only one product
   * No layout shift since component was never visible
   */
  removeComponent() {
    if (this.element) {
      // Remove from DOM - no layout shift since it was collapsed
      this.element.remove();
      console.log("SKU Flavor Selector: Component removed (no shift)");
    }
  }

  /**
   * @deprecated Use removeComponent() instead
   */
  hideComponent() {
    this.removeComponent();
  }
}

/**
 * Initialize all SKU Flavor Selectors on the page
 */
function initSKUFlavorSelectors() {
  const selectors = document.querySelectorAll(".sku-flavor-selector");

  selectors.forEach((element) => {
    new SKUFlavorSelector(element);
  });
}

// Initialize on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initSKUFlavorSelectors);
} else {
  initSKUFlavorSelectors();
}

// Re-initialize on dynamic content load (for AJAX-loaded product pages)
document.addEventListener("shopify:section:load", initSKUFlavorSelectors);
